import React, { useEffect } from 'react';

import { Link, useSearchParams } from 'react-router-dom';

import { getResultHit, TFPageContent, TFPageHeader } from '@tf/shared';
import {
	Box,
	createStyles,
	TFDataTable,
	TFLoadingOverlay,
	TFText,
	UnstyledButton,
	useLocalStorage,
} from '@tf/ui';
import { S, T } from '@tf/utils';

import { usePayment, useTransactionAssessmentResults } from '@/core/api/payments';
import { appStore } from '@/core/stores';

import { usePaymentAssessmentParams } from './usePaymentAssessmentParams';

const useStyles = createStyles(({ colors }) => ({
	link: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

export const ScreeningsTable = () => {
	const { classes } = useStyles();

	const [searchParams] = useSearchParams();

	const { paymentObjectName, assessmentObjectName } = usePaymentAssessmentParams();

	const initialFilters = [
		{ id: 'type', value: searchParams.get('type') },
		{ id: searchParams.get('filter') as string, value: 'true' },
	].filter((f) => f.value && f.id);

	const [filtersState, setFiltersState] = useLocalStorage<
		{
			id: string;
			value: unknown;
		}[]
	>({
		defaultValue: [],
		key: 'screening-results-table-filters',
		getInitialValueInEffect: false,
	});

	useEffect(() => {
		setFiltersState(initialFilters);
	}, [initialFilters.length]);

	const { data, isPending } = usePayment(paymentObjectName);
	const { data: assessmentResults, isPending: isAssessmentsPending } =
		useTransactionAssessmentResults(paymentObjectName);

	const tenant = appStore.tenant?.get();
	T.assertNotNullish(tenant, 'no tenant in appStore');

	const assessmentResult = assessmentResults?.find((assessment) =>
		assessment.id.includes(assessmentObjectName)
	);

	if (isPending || !data || !assessmentResults || !assessmentResult) {
		return <TFLoadingOverlay />;
	}

	const actorData =
		assessmentResult.data.raw_screening_response?.actor.rawResponse.hits?.map((hit) =>
			buildScreeningItem(hit, 'actor')
		) ?? [];
	const bankData =
		assessmentResult.data.raw_screening_response?.bank.rawResponse.hits?.map((hit) =>
			buildScreeningItem(hit, 'bank')
		) ?? [];
	const dataWithAppliedFilters = [...actorData, ...bankData].filter((item) => {
		if (filtersState.length === 0) return true;

		let matchesAll = true;
		for (const filter of filtersState) {
			if (filter.id === 'type') {
				matchesAll = matchesAll && item.type === filter.value;
			} else if (filter.id === 'sanction') {
				matchesAll = matchesAll && item.sanction === (filter.value === 'true');
			} else if (filter.id === 'pep') {
				matchesAll = matchesAll && item.pep === (filter.value === 'true');
			} else if (filter.id === 'adverseMedia') {
				matchesAll = matchesAll && item.adverseMedia === (filter.value === 'true');
			}
		}
		return matchesAll;
	});

	return (
		<>
			<TFPageHeader
				tenant={tenant}
				title="Assessment screening results"
				breadcrumbs={[
					{ href: `/payments`, title: 'Payments' },
					{ href: `/payments/${paymentObjectName}`, title: data.labels.order_id },
				]}
			/>
			<TFPageContent>
				<Box>
					<TFDataTable<(typeof bankData)[0]>
						name="payments-screenings-list"
						isLoading={isAssessmentsPending}
						data={dataWithAppliedFilters}
						serverFiltering={{
							onFiltersChange: setFiltersState,
							state: filtersState,
						}}
						defs={[
							{
								header: 'Name',
								accessorKey: 'hit.keyInfo.name',
								Cell: ({ row }) => {
									const { hit } = row.original;
									return (
										<UnstyledButton
											component={Link}
											to={`/payments/${paymentObjectName}/assessments/${assessmentObjectName}/screenings/${hit.id}`}
											className={classes.link}
										>
											<TFText inherit lineClamp={1}>
												{hit.keyInfo.name ?? 'N/A'}
											</TFText>
										</UnstyledButton>
									);
								},
							},
							{
								header: 'Type',
								accessorKey: 'type',
								size: 20,
								Cell: ({ row }) => {
									const { type } = row.original;
									return (
										<TFText inherit lineClamp={1}>
											{S.prettify(type ?? 'N/A')}
										</TFText>
									);
								},
							},
							{
								header: 'Sanctioned',
								accessorKey: 'sanction',
								size: 40,
								Cell: ({ row }) => {
									const { sanction } = row.original;
									return (
										<TFText inherit lineClamp={1}>
											{sanction ? 'Yes' : 'No'}
										</TFText>
									);
								},
							},
							{
								header: 'Is adverse media',
								accessorKey: 'adverseMedia',
								size: 40,
								Cell: ({ row }) => {
									const { adverseMedia } = row.original;
									return (
										<TFText inherit lineClamp={1}>
											{adverseMedia ? 'Yes' : 'No'}
										</TFText>
									);
								},
							},
							{
								header: 'Pep',
								accessorKey: 'pep',
								size: 40,
								Cell: ({ row }) => {
									const { pep } = row.original;
									return (
										<TFText inherit lineClamp={1}>
											{pep ? 'Yes' : 'No'}
										</TFText>
									);
								},
							},
						]}
						availableFilters={[
							{
								name: 'type',
								label: 'Type',
								type: 'select',
								value: [
									{ value: 'actor', label: 'Actor' },
									{ value: 'bank', label: 'Bank' },
								],
							},
							{
								name: 'sanction',
								label: 'Sanctioned',
								type: 'select',
								value: [
									{ value: 'false', label: 'No' },
									{ value: 'true', label: 'Yes' },
								],
							},
							{
								name: 'pep',
								label: 'Pep',
								type: 'select',
								value: [
									{ value: 'false', label: 'No' },
									{ value: 'true', label: 'Yes' },
								],
							},
							{
								name: 'adverseMedia',
								label: 'Is adverse media',
								type: 'select',
								value: [
									{ value: 'false', label: 'No' },
									{ value: 'true', label: 'Yes' },
								],
							},
						]}
					/>
				</Box>
			</TFPageContent>
		</>
	);
};

const buildScreeningItem = (hit: any, typeLabel: string) => ({
	hit: getResultHit(hit),
	type: typeLabel,
	sanction: hit.doc.types.some((type: string) => type.startsWith('sanction')),
	pep: hit.doc.types.some((type: string) => type.startsWith('pep')),
	adverseMedia: hit.doc.types.some((type: string) => type.startsWith('adverse-media')),
});
